/**
 * AdminPrayerRequestsPage — moderation queue for the `prayer_requests` block.
 *
 * Lets admins:
 *   • See pending + approved + archived requests across each site
 *   • Approve and publish to the public wall (only if the submitter ticked
 *     "share publicly")
 *   • Approve as private (visible only in admin)
 *   • Archive / delete
 *
 * Today this is wired against the Bishop Manjoro slug; if more sites adopt
 * the prayer block we can add a slug selector at the top.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, Check, Globe, Lock, Archive, Trash2, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const SLUG_OPTIONS = [
  { slug: 'bishop-manjoro', label: 'Bishop Manjoro' },
  { slug: 'abmhc',          label: 'ABMHC' },
];

const AdminPrayerRequestsPage = () => {
  const [slug, setSlug] = useState(SLUG_OPTIONS[0].slug);
  const qc = useQueryClient();

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['admin-prayers', slug],
    queryFn: () => api.get(`/sites/admin/${slug}/prayer-requests`).then((r) => r.data),
  });

  const moderate = useMutation({
    mutationFn: ({ id, ...patch }) => api.post(`/sites/admin/${slug}/prayer-request/${id}/moderate`, patch),
    onSuccess: () => {
      toast.success('Updated');
      qc.invalidateQueries({ queryKey: ['admin-prayers', slug] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Update failed'),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/sites/admin/${slug}/prayer-request/${id}`),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['admin-prayers', slug] });
    },
  });

  const buckets = {
    pending: items.filter((p) => p.status === 'pending'),
    approved: items.filter((p) => p.status === 'approved'),
    archived: items.filter((p) => p.status === 'archived'),
  };

  const card = (p) => (
    <Card key={p.id} className="mb-3" data-testid={`prayer-card-${p.id}`}>
      <CardContent className="pt-4 pb-3 space-y-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{p.name || 'Anonymous'}</span>
            <Badge variant={p.status === 'pending' ? 'destructive' : p.status === 'approved' ? 'default' : 'secondary'}>
              {p.status}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {p.visibility === 'public' ? <Globe className="h-3 w-3 mr-1" /> : <Lock className="h-3 w-3 mr-1" />}
              {p.visibility}
            </Badge>
            {p.is_public_requested && p.visibility !== 'public' && (
              <Badge variant="outline" className="text-xs text-amber-700 border-amber-200">
                wants public
              </Badge>
            )}
          </div>
          <p className="text-xs text-slate-500">{p.submitted_at?.slice(0, 16).replace('T', ' ')}</p>
        </div>
        <p className="text-sm text-slate-700 whitespace-pre-line">{p.message}</p>
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          {p.status !== 'approved' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => moderate.mutate({ id: p.id, status: 'approved', visibility: 'private' })}
              data-testid={`prayer-approve-private-${p.id}`}
            >
              <Check className="h-3.5 w-3.5 mr-1" /> Approve (private)
            </Button>
          )}
          {p.is_public_requested && p.visibility !== 'public' && (
            <Button
              size="sm"
              onClick={() => moderate.mutate({ id: p.id, status: 'approved', visibility: 'public' })}
              data-testid={`prayer-approve-public-${p.id}`}
            >
              <Globe className="h-3.5 w-3.5 mr-1" /> Publish to wall
            </Button>
          )}
          {p.visibility === 'public' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => moderate.mutate({ id: p.id, visibility: 'private' })}
              data-testid={`prayer-make-private-${p.id}`}
            >
              <Lock className="h-3.5 w-3.5 mr-1" /> Make private
            </Button>
          )}
          {p.status !== 'archived' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => moderate.mutate({ id: p.id, status: 'archived' })}
              data-testid={`prayer-archive-${p.id}`}
            >
              <Archive className="h-3.5 w-3.5 mr-1" /> Archive
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="text-red-600 hover:text-red-700"
            onClick={() => window.confirm('Delete this prayer request permanently?') && remove.mutate(p.id)}
            data-testid={`prayer-delete-${p.id}`}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
          </Button>
        </div>
        {p.prayed_count > 0 && (
          <p className="text-xs text-slate-500">🙏 {p.prayed_count} people prayed</p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-prayer-page">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Inbox className="h-5 w-5 text-slate-500" />
          Prayer Request Moderation
        </h1>
        <div className="flex gap-2">
          {SLUG_OPTIONS.map((opt) => (
            <Button
              key={opt.slug}
              size="sm"
              variant={slug === opt.slug ? 'default' : 'outline'}
              onClick={() => setSlug(opt.slug)}
              data-testid={`prayer-slug-${opt.slug}`}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="py-20 text-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading…
        </div>
      ) : (
        <Tabs defaultValue="pending">
          <TabsList>
            <TabsTrigger value="pending" data-testid="prayer-tab-pending">
              Pending {buckets.pending.length > 0 && `(${buckets.pending.length})`}
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="prayer-tab-approved">
              Approved ({buckets.approved.length})
            </TabsTrigger>
            <TabsTrigger value="archived" data-testid="prayer-tab-archived">
              Archived ({buckets.archived.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="pending" className="pt-4">
            {buckets.pending.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-sm text-slate-500">No pending requests.</CardContent></Card>
            ) : buckets.pending.map(card)}
          </TabsContent>
          <TabsContent value="approved" className="pt-4">
            {buckets.approved.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-sm text-slate-500">No approved requests yet.</CardContent></Card>
            ) : buckets.approved.map(card)}
          </TabsContent>
          <TabsContent value="archived" className="pt-4">
            {buckets.archived.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-sm text-slate-500">Nothing archived.</CardContent></Card>
            ) : buckets.archived.map(card)}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default AdminPrayerRequestsPage;
